// OrganizationsDashboard.js
import React, { useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, List, ListItem, 
  ListItemText, Chip, Divider, Grid
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const OrganizationsDashboard = ({ organizations }) => {
  // Calculate last 30 days signups
  const last30DaysData = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recent = organizations.filter(org => 
      new Date(org.created_at) >= thirtyDaysAgo
    );

    // Group by day for trend analysis
    const dailySignups = {};
    recent.forEach(org => {
      const date = new Date(org.created_at).toDateString();
      dailySignups[date] = (dailySignups[date] || 0) + 1;
    });

    const avgDaily = recent.length / 30;
    
    return {
      total: recent.length,
      daily: dailySignups,
      avgDaily: Math.round(avgDaily * 10) / 10
    };
  }, [organizations]);

  // Get latest signups
  const latestSignups = useMemo(() => {
    return [...organizations]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10);
  }, [organizations]);

  // Helper function to format relative time
  const getRelativeTime = (date) => {
    const now = new Date();
    const orgDate = new Date(date);
    const diffInHours = Math.floor((now - orgDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    return orgDate.toLocaleDateString();
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {/* Last 30 Days Signups Widget */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Last 30 Days Signups
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                <Typography variant="h3" component="div" sx={{ mr: 1 }}>
                  {last30DaysData.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  organizations
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Daily average:
                </Typography>
                <Chip 
                  label={`${last30DaysData.avgDaily}/day`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Last 30 days activity
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Latest Signups Widget */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6" component="h2">
                  Latest Signups
                </Typography>
              </Box>

              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {latestSignups.map((org, index) => (
                  <ListItem 
                    key={org.organization_id}
                    sx={{ 
                      px: 0,
                      py: 1,
                      borderBottom: index < latestSignups.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" noWrap sx={{ maxWidth: '60%' }}>
                            {org.name}
                          </Typography>
                          <Chip 
                            label={getRelativeTime(org.created_at)} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {org.contact_email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {org.address}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {latestSignups.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No organizations found
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationsDashboard;